import '../styles/LeadsLeadRowsPanel.css';
import React from 'react';
import { useProjectCollection } from '../lib/projectData';

const COLUMN_LABELS = ['Name', 'Role', 'Company', 'Location', 'Profile'];

function zeroPad(n) {
  return String(n).padStart(2, '0');
}

function externalProfileHref(profileUrl) {
  if (typeof profileUrl !== 'string' || profileUrl.trim() === '') return null;
  const trimmed = profileUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return `https://${trimmed}`;
}

function LeadRowGrid({ rows }) {
  return (
    <ol className="llr-rows" role="list" aria-label="Lead rows">
      {rows.map((lead, index) => {
        const href = externalProfileHref(lead.profileUrl);
        return (
          <li className="llr-row" key={lead.id}>
            <p className="llr-index" aria-hidden="true">{zeroPad(index + 1)}</p>
            <h3 className="llr-name">{lead.name}</h3>
            <dl className="llr-fields">
              <div className="llr-field llr-field--role">
                <dt className="llr-label">Role</dt>
                <dd className="llr-value">{lead.role || '—'}</dd>
              </div>
              <div className="llr-field llr-field--company">
                <dt className="llr-label">Company</dt>
                <dd className="llr-value">{lead.company || '—'}</dd>
              </div>
              <div className="llr-field llr-field--location">
                <dt className="llr-label">Location</dt>
                <dd className="llr-value">{lead.location || '—'}</dd>
              </div>
              <div className="llr-field llr-field--profile">
                <dt className="llr-label">Profile</dt>
                <dd className="llr-value">
                  {href ? (
                    <a
                      className="llr-profile"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open LinkedIn profile for ${lead.name}`}
                    >
                      <span>{lead.profileUrl}</span>
                      <span className="llr-profile-arrow" aria-hidden="true">↗</span>
                    </a>
                  ) : (
                    <span>—</span>
                  )}
                </dd>
              </div>
            </dl>
          </li>
        );
      })}
    </ol>
  );
}

function RowsLoadingState() {
  return (
    <div className="llr-status" data-presentation-state="loading" aria-live="polite" aria-busy="true">
      <p className="llr-loading-label">
        <span className="llr-loading-dot" aria-hidden="true" />
        Loading lead rows
      </p>
      <div className="llr-loading" aria-hidden="true">
        <div className="llr-loading-line">
          <span className="llr-loading-bar" />
        </div>
        <div className="llr-loading-line">
          <span className="llr-loading-bar llr-loading-bar--short" />
        </div>
        <div className="llr-loading-line">
          <span className="llr-loading-bar" />
        </div>
      </div>
    </div>
  );
}

function LeadsLeadRowsPanel({ selectedRunId }) {
  const { data, loading, error, refresh } = useProjectCollection('leads');
  const { data: runs, loading: runsLoading } = useProjectCollection('extraction_runs');

  const leads = Array.isArray(data) ? data : [];
  const savedRuns = (Array.isArray(runs) ? runs : [])
    .slice()
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  const selectedRun = savedRuns.find((run) => run.id === selectedRunId) || null;
  const rows = selectedRun
    ? leads.filter((lead) => lead.runId === selectedRun.id)
    : [];

  const state = loading ? 'loading' : error ? 'error' : rows.length ? 'populated' : 'empty';

  return (
    <section
      className="llr-root"
      aria-labelledby="llr-title"
      data-content-rail="primary"
    >
      <div className="llr-panel" data-state={state}>
        <div className="llr-head">
          <h2 className="llr-title" id="llr-title">Lead rows</h2>
          <p className="llr-head-meta">
            <span
              className={`llr-head-dot${selectedRun ? '' : ' llr-head-dot--idle'}`}
              aria-hidden="true"
            />
            {selectedRun ? selectedRun.title : 'No saved list'}
          </p>
        </div>

        <div className="llr-columns" role="presentation" aria-hidden="true">
          <p className="llr-columns-label">{COLUMN_LABELS.join(' / ')}</p>
        </div>

        {state === 'loading' || runsLoading ? (
          <RowsLoadingState />
        ) : error ? (
          <div className="llr-status" role="alert">
            <p className="llr-message">
              <strong>Lead rows could not be loaded</strong>
              {error && typeof error === 'string' ? `${error} ` : 'The saved leads did not arrive from the store. '}
              <button
                type="button"
                className="llr-retry"
                onClick={() => {
                  refresh();
                }}
              >
                Retry
              </button>
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="llr-status">
            <p className="llr-message">
              <strong>{selectedRun ? 'No lead rows for this run' : 'No lead rows yet'}</strong>
              {selectedRun
                ? `${selectedRun.title} returned no leads, so there are no rows to read.`
                : 'Once a saved extraction run exists, its leads appear here.'}
            </p>
          </div>
        ) : (
          <>
            <span className="llr-sr-only">
              {rows.length} lead rows for {selectedRun ? selectedRun.title : 'the saved list'}
            </span>
            <LeadRowGrid rows={rows} />
          </>
        )}
      </div>
    </section>
  );
}

export default LeadsLeadRowsPanel;